// @ts-strict-ignore
import * as React from 'react';
import Modal from 'antd/lib/modal/Modal';
import Form from 'antd/lib/form';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { selectAppStoreType } from '../../selectors/selectors';
import Input from 'antd/lib/input';
import { globalMessage } from '../../utils/globalMessage';
import { useUploadFormWithFile } from '../ciQuickstart/hooks/useUploadFormWithFile';
import { BucketComponentProps } from '../../typescriptHelpers';
import { RcFile } from 'antd/lib/upload';
import Dragger from 'antd/lib/upload/Dragger';
import { BuildDistroBuild } from '../../types';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import { antGrey } from '../styles/globalStyles';
import { appEndpoint } from '../../helpers/apiHelpers';
import { uploadBuildDistributionBuildSuccess } from '../../actions/buildDistribution';
import { supportedBuildFileTypesByAppStoreType } from '../../helpers';

export interface UploadNewBuildModalProps extends BucketComponentProps {
    open: boolean;
    onCancel: () => void;
    bucketName: string;
}

type FormData = {
    testerNotes: string;
};

export const UploadNewBuildModal: React.FC<UploadNewBuildModalProps> = ({
    appId,
    orgId,
    bucketId,
    open,
    onCancel,
    bucketName,
}) => {
    const [form] = Form.useForm<FormData>();

    const [uploadResult, uploadIsLoading, uploadError, upload] = useUploadFormWithFile<FormData, BuildDistroBuild>();

    const [rcFile, setRcFile] = React.useState<RcFile>();

    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));

    const dispatch = useDispatch();

    React.useEffect(() => {
        if (uploadResult?.bucketId) {
            globalMessage.success('Build uploaded successfully!');
            dispatch(
                uploadBuildDistributionBuildSuccess({
                    appId,
                    orgId,
                    build: uploadResult,
                    bucketId: uploadResult.bucketId,
                }),
            );
            onCancel();
        }
    }, [uploadResult]);

    React.useEffect(() => {
        if (uploadError) {
            globalMessage.error(uploadError.message ?? 'Failed to upload build');
        }
    }, [uploadError]);

    const acceptedFileTypes = supportedBuildFileTypesByAppStoreType(appStoreType);

    const appUrl = appEndpoint(orgId, appId);

    return (
        <Modal
            open={open}
            title={`Upload a new build to '${bucketName}'`}
            okText="Save"
            onOk={() => {
                form.validateFields()
                    .then(() => {
                        form.submit();
                    })
                    .catch(() => null);
            }}
            okButtonProps={{ loading: uploadIsLoading }}
            onCancel={onCancel}
            destroyOnClose
        >
            <Dragger
                accept={acceptedFileTypes.join(', ')}
                multiple={false}
                beforeUpload={(file) => {
                    setRcFile(file);
                    // prevent default upload
                    return false;
                }}
                onRemove={() => {
                    setRcFile(undefined);
                }}
                maxCount={1}
                style={{
                    borderWidth: 4,
                    marginBottom: 16,
                }}
            >
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        height: 120,
                        alignItems: 'center',
                    }}
                >
                    <Space direction="vertical">
                        <Text style={{ color: antGrey.grey7 }}>
                            Click or drag an {acceptedFileTypes.join(' or ')}{' '}
                            {acceptedFileTypes.length > 1 ? 'file' : 'file'} {`here to add to the bucket`}
                        </Text>
                    </Space>
                </div>
            </Dragger>
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    testerNotes: '',
                }}
                onFinish={(values) => {
                    upload({
                        file: rcFile,
                        formValues: values,
                        uploadUrl: `${window.location.origin}/${appUrl}/build-distribution/bucket/${bucketId}/build`,
                    });
                }}
                preserve={false}
            >
                <Form.Item label="Enter tester notes for this build." name="testerNotes">
                    <Input.TextArea rows={4} placeholder="This is the best build yet!" />
                </Form.Item>
            </Form>
        </Modal>
    );
};
